import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { OPEN_SETTINGS_EVENT, analyticsEnabled, gaEnabled, getConsent, grantAndLoad, isPublicPath, setConsent, trackPageView } from "@/lib/analytics";
import { clarityEnabled, setClarityMask } from "@/lib/clarity";

const TOOLS = {
  ga: "Google Analytics",
  clarity: "Microsoft Clarity (which records how pages are used, with form entries hidden)",
};

// Names only the tools actually enabled on this site.
export function notice() {
  const tools = [gaEnabled() && TOOLS.ga, clarityEnabled() && TOOLS.clarity].filter(Boolean);
  const off = tools.length > 1 ? "Both stay off unless you accept." : "It stays off unless you accept.";
  return `We use ${tools.join(" and ")} to understand how visitors use Cyannect and improve the site. ${off}`;
}

export const AnalyticsConsent = () => {
  const { pathname } = useLocation();
  const enabled = analyticsEnabled();
  const [choice, setChoice] = useState(() => (enabled ? getConsent() : "n/a"));
  const lastTracked = useRef(null);

  useEffect(() => { if (enabled && getConsent() === "granted") grantAndLoad(); }, [enabled]);
  useEffect(() => {
    const reopen = () => setChoice(null);
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, []);
  // Small delay so page titles (Helmet) settle before the hit is sent.
  useEffect(() => {
    setClarityMask(!isPublicPath(pathname));
    if (enabled) window[`ga-disable-${process.env.REACT_APP_GA_MEASUREMENT_ID}`] = !isPublicPath(pathname) || getConsent() !== "granted";
    const t = setTimeout(() => {
      if (lastTracked.current === pathname || getConsent() !== "granted") return;
      lastTracked.current = pathname;
      trackPageView(pathname);
    }, 300);
    return () => clearTimeout(t);
  }, [pathname, choice]);

  if (!enabled || choice) return null;
  const portal = /^\/(merchant|admin|reseller)(\/|$)/.test(pathname);
  const current = getConsent();
  const decide = (v) => { setConsent(v); setChoice(v); };
  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie consent" data-testid="analytics-consent-banner"
      className={`fixed bottom-4 left-4 right-4 ${portal ? "sm:left-auto sm:right-6 sm:bottom-24" : "sm:right-auto"} sm:max-w-md z-[45] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl p-4 text-sm text-slate-700`}>
      <p className="font-semibold text-slate-900">Cookies &amp; analytics</p>
      <p className="mt-1" data-testid="analytics-consent-text">{notice()}</p>
      {current && <p className="mt-1 text-xs text-slate-500" data-testid="analytics-consent-current">Current choice: {current === "granted" ? "Accepted" : "Declined"}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={() => decide("granted")} data-testid="analytics-consent-accept"
          className="rounded-full bg-slate-900 text-white px-4 py-1.5 font-semibold transition-colors hover:bg-slate-700">Accept</button>
        <button type="button" onClick={() => decide("denied")} data-testid="analytics-consent-decline"
          className="rounded-full border border-slate-300 px-4 py-1.5 font-semibold transition-colors hover:bg-slate-100">Decline</button>
      </div>
    </div>
  );
};
